import { useState, useEffect } from 'react';
import { MessageCircle, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

export default function LiveChatPanel() {
  const [config, setConfig]   = useState(null);
  const [form, setForm]       = useState({ propertyId: '', widgetId: '', isEnabled: true, position: 'br', welcomeMessage: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    api.get('/broker/live-chat/config')
      .then(res => {
        const c = res.data.data;
        if (c) {
          setConfig(c);
          setForm({
            propertyId:     c.property_id     || '',
            widgetId:       c.widget_id       || '',
            isEnabled:      c.is_enabled      ?? true,
            position:       c.position        || 'br',
            welcomeMessage: c.welcome_message || '',
          });
        }
      })
      .catch(() => toast.error('Failed to load live chat config'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.propertyId.trim() || !form.widgetId.trim()) {
      toast.error('Property ID and Widget ID are required');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/broker/live-chat/config', form);
      setConfig(res.data.data);
      toast.success('Live chat settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isConfigured = config?.property_id && config?.widget_id;

  return (
    <div style={{ maxWidth: 600 }}>
      {/* Status banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
        borderRadius: 8, marginBottom: 24,
        background: isConfigured && config?.is_enabled ? 'rgba(34,197,94,0.08)' : 'var(--bg-secondary)',
        border: `1px solid ${isConfigured && config?.is_enabled ? 'var(--accent-success)' : 'var(--border-color)'}`,
      }}>
        {isConfigured && config?.is_enabled
          ? <CheckCircle size={16} color="var(--accent-success)" />
          : <AlertCircle size={16} color="var(--text-muted)" />
        }
        <span style={{ fontSize: 14, color: isConfigured && config?.is_enabled ? 'var(--accent-success)' : 'var(--text-muted)' }}>
          {isConfigured && config?.is_enabled
            ? 'Live chat is active and visible to your traders'
            : 'Live chat is not configured or disabled'}
        </span>
      </div>

      {/* How to get credentials */}
      <div style={{
        padding: '12px 16px', background: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)', borderRadius: 8, marginBottom: 24,
      }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <MessageCircle size={14} color="var(--accent-primary)" /> How to get your Tawk.to credentials
        </div>
        <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
          <li>Go to <a href="https://tawk.to" target="_blank" rel="noreferrer" style={{ color: 'var(--accent-primary)' }}>tawk.to <ExternalLink size={10} /></a> and create a free account</li>
          <li>Create a new property for your brokerage</li>
          <li>Go to <strong>Administration → Chat Widget</strong></li>
          <li>Your Property ID and Widget ID are in the embed code snippet</li>
        </ol>
        <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--bg-tertiary)', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>
          {'<!-- look for these values in the embed code -->'}<br />
          {'Tawk_API.propertyId = "'}
          <span style={{ color: 'var(--accent-primary)' }}>YOUR_PROPERTY_ID</span>
          {'"'}<br />
          {'Tawk_API.widgetId = "'}
          <span style={{ color: 'var(--accent-success)' }}>YOUR_WIDGET_ID</span>
          {'"'}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>Loading…</div>
      ) : (
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gap: 16 }}>
            <Field label="Property ID" required>
              <input
                value={form.propertyId}
                onChange={e => setForm(p => ({ ...p, propertyId: e.target.value.trim() }))}
                placeholder="e.g. 5f3c8a1d4a7f8b0001234abc"
                style={inputStyle}
              />
            </Field>

            <Field label="Widget ID" required>
              <input
                value={form.widgetId}
                onChange={e => setForm(p => ({ ...p, widgetId: e.target.value.trim() }))}
                placeholder="e.g. default"
                style={inputStyle}
              />
            </Field>

            <Field label="Welcome Message" hint="Optional — shown in the chat widget">
              <input
                value={form.welcomeMessage}
                onChange={e => setForm(p => ({ ...p, welcomeMessage: e.target.value }))}
                placeholder="Hi! How can we help you today?"
                maxLength={200}
                style={inputStyle}
              />
            </Field>

            <Field label="Widget Position">
              <select
                value={form.position}
                onChange={e => setForm(p => ({ ...p, position: e.target.value }))}
                style={inputStyle}
              >
                <option value="br">Bottom Right</option>
                <option value="bl">Bottom Left</option>
                <option value="tr">Top Right</option>
                <option value="tl">Top Left</option>
              </select>
            </Field>

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.isEnabled}
                onChange={e => setForm(p => ({ ...p, isEnabled: e.target.checked }))}
              />
              <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>Enable live chat for traders</span>
            </label>
          </div>

          <button type="submit" disabled={saving} style={{
            marginTop: 24, padding: '10px 24px', background: saving ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
            color: saving ? 'var(--text-muted)' : '#fff', border: 'none', borderRadius: 8,
            fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer',
          }}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </form>
      )}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
        {label} {required && <span style={{ color: 'var(--accent-danger)' }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>— {hint}</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '9px 12px', background: 'var(--bg-secondary)',
  color: 'var(--text-primary)', border: '1px solid var(--border-color)',
  borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};